// ============================================================
// 基座 manifest 规范（阶段一 T3）：dtwince.dtbase.schema.json 的 V1 子集
// 目录内置基座与阶段二 .dtbase 资产包共用同一 manifest 字段，迁移零改造
// ============================================================
import { z } from 'zod';
import { ASSET_TYPES, BASE_TIERS, BASE_VISIBILITIES, GENERATION_SCHEMA_VERSION } from './constants';

const semverSchema = z.string().regex(/^\d+\.\d+\.\d+$/, '版本号需形如 1.0.0');

/** 点位契约：基座声明"需要哪些数据点"，AI 生成据此做点位映射 */
export const basePointSchema = z
  .object({
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    unit: z.string().max(32).optional(),
    type: z.enum(['number', 'string', 'boolean']).default('number'),
    recommend: z
      .object({ warn: z.number().optional(), critical: z.number().optional() })
      .strict()
      .optional(),
  })
  .strict();
export type BasePoint = z.infer<typeof basePointSchema>;

/** 规则链模板引用（flow_pack / 基座内置模板） */
export const baseFlowTemplateSchema = z
  .object({
    ref: z.string().min(1).max(64),
    params: z.record(z.unknown()).default({}),
  })
  .strict();

/** 模型引用（基座允许使用的模型资源白名单） */
export const baseModelRefSchema = z
  .object({
    key: z.string().min(1).max(64),
    name: z.string().max(120).optional(),
    url: z.string().min(1).max(500),
    format: z.string().max(10).default('glb'),
  })
  .strict();

export const baseManifestSchema = z
  .object({
    manifestVersion: z.literal('1.0'),
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z0-9][a-z0-9._-]*$/i, 'id 仅允许字母数字与 . _ -'),
    name: z.string().min(1).max(120),
    type: z.enum(ASSET_TYPES),
    industry: z.array(z.string().max(40)).max(10).default([]),
    version: semverSchema,
    schemaVersion: z.literal(GENERATION_SCHEMA_VERSION),
    minKernelVersion: semverSchema.optional(),
    description: z.string().max(1000).optional(),
    tier: z.enum(BASE_TIERS).default('pro'),
    visibility: z.enum(BASE_VISIBILITIES).default('public'),
    points: z.array(basePointSchema).max(200).default([]),
    flowTemplates: z.array(baseFlowTemplateSchema).max(50).default([]),
    modelRefs: z.array(baseModelRefSchema).max(100).default([]),
    deps: z
      .array(
        z
          .object({
            id: z.string().min(1).max(64),
            version: z.string().min(1).max(40),
            type: z.enum(ASSET_TYPES).optional(),
          })
          .strict(),
      )
      .max(50)
      .default([]),
    tags: z.array(z.string().max(40)).max(20).default([]),
    i18n: z
      .record(z.object({ name: z.string().max(120) }).passthrough())
      .optional(),
  })
  .strict();

export type BaseManifest = z.infer<typeof baseManifestSchema>;
